var React = require('react');
var dayjs = require('../../utils/dayjsConfig');
var sensorsManager = require('sensorsManager');
var { fertilizerNameToDisplay } = require('nitrogenManager');
var { upper, parseShortFloat } = require('@managers/helpers');
var {
  nitroUnitsToFertUnits,
  fertUnitsToNitroUnits,
} = require('@managers/unitsManager');
var { buildPressureRow, formatCardDateTime } = require('./eventPopUpHelpers');
var { buildGraphEventCard } = require('./graphEventCard');

// Icons for the redesigned graph popup (Figma 12009:11615). Module scope so the
// webpack requires are not re-run for every hovered event.
const GRAPH_CARD_ICONS = {
  irrigation: require('images/icons/graph/irrigation.svg'),
  fertilization: require('images/icons/planner/fert_icon_grey.svg'),
  rain: require('images/icons/graph/rain_icon.svg'),
  timer: require('images/icons/graph/timer.svg'),
  piezometer: require('images/icons/graph/piezometer.svg'),
  pressureAlert: require('images/icons/graph/pressure_alert.svg'),
};

export function EventPopUp(
  startTime,
  endTime,
  eventType,
  unit,
  value,
  sourceObj,
  shouldDisplayTrashIcon,
  codeSource,
  timezone,
  t,
  nitrogenInFertUnits = '',
  pressure = null,
  pressureUnit = '',
  // { useNewDesign, pressureAlert } — opt-in for the redesigned graph card.
  // Callers that omit it get the legacy popup byte for byte.
  options = {},
) {
  let begDate = new Date(startTime);
  let endDate = new Date(endTime);
  let fullBegDateStr, fullBegTimeStr, fullEndDateStr, fullEndTimeStr;
  if (codeSource === 'planner') {
    fullBegDateStr = dayjs(begDate).format('MMM D').toUpperCase();
    fullBegTimeStr = dayjs(begDate).format('hh:mm a');
    fullEndDateStr = dayjs(endDate).format('MMM D').toUpperCase();
    fullEndTimeStr = dayjs(endDate).format('hh:mm a');
  } else {
    fullBegDateStr = dayjs(begDate).tz(timezone).format('MMM D').toUpperCase();
    fullBegTimeStr = dayjs(begDate).tz(timezone).format('hh:mm a');
    fullEndDateStr = dayjs(endDate).tz(timezone).format('MMM D').toUpperCase();
    fullEndTimeStr = dayjs(endDate).tz(timezone).format('hh:mm a');
  }
  let imgSrc;
  let deleteRow = '';
  let source = '';
  let firstRow = '';
  let extraRow = '';
  // Plain-text twin of extraRow (fertilization's "CONTAINING x kg/ha"), so the
  // redesigned card can render that line in its own styling instead of
  // inheriting the legacy markup's padding.
  let extraText = '';
  let hideEndDate = false;
  // Only sensor-sourced events get an icon in the redesigned card's source row;
  // manual entries show the name alone (see graphEventCard.buildSourceRow).
  let sourceIconSrc = null;
  value = parseFloat(value).toFixed(3) * 1;

  if (eventType === 'irrigation') {
    imgSrc = require('images/icons/graph/irrigation.svg');
    if (sourceObj.hasOwnProperty('source')) {
      if (sourceObj.hasOwnProperty('manufacturer')) {
        source = sensorsManager.sensorsMap.get(
          sourceObj.source + '_' + sourceObj.manufacturer,
        );
      } else {
        source = sensorsManager.sensorsMap.get(sourceObj.source);
      }
      sourceIconSrc = source ? GRAPH_CARD_ICONS.piezometer : null;
    } else {
      source = sourceObj.hasOwnProperty('type') ? 'Automatic' : 'Manual';
    }
    let disableEventRecovery = ['piezometer', 'wfr_ndrip'].includes(
      sourceObj.source,
    );
    let sensorId = sourceObj.sensor_id;
    deleteRow =
      "<div style='display: flex;align-self: end;padding-right: 8px;padding-left: 8px;justify-self: self-end' class=" +
      (shouldDisplayTrashIcon ? '' : 'd-none') +
      "><a href='#'><img id='deleteIrrigationButton' date='" +
      startTime +
      "' disableEventRecovery='" +
      disableEventRecovery +
      "' sensorId='" +
      sensorId +
      "' src='" +
      require('images/icons/graph/trash_graph.svg') +
      "'/></a></div>";
    // Pressure-sensor irrigation events carry a pressure reading. Show it as a
    // second line under the irrigation amount, matching the reported-events
    // format (`value+unit (Pressure)`) and the first line's design.
    extraRow = buildPressureRow(
      pressure,
      pressureUnit,
      t ? t('pressure') : 'Pressure',
    );
  } else if (eventType === 'fertilization') {
    imgSrc = require('images/icons/planner/fert_icon_grey.svg');
    if (sourceObj.fertType && sourceObj.fertAmount) {
      nitrogenInFertUnits = fertUnitsToNitroUnits(nitrogenInFertUnits);
      if (!['no3', 'nh4'].includes(sourceObj.fertType)) {
        extraText =
          upper(t('containing')) + ' ' + value + ' ' + nitrogenInFertUnits;
        extraRow =
          "<div class='pl-2 mr-1 text-grey71 font-weight-semi-bold' style='font-size: 14px;white-space: nowrap'>" +
          extraText +
          '</div>';
      }
      value =
        fertilizerNameToDisplay(sourceObj.fertType) +
        ': ' +
        parseShortFloat(sourceObj.fertAmount);
      unit = nitroUnitsToFertUnits(unit);
    }
    deleteRow =
      "<div style='align-self-center; display: flex;align-self: end;padding-right: 8px;padding-left: 8px;justify-self: self-end' class=" +
      (shouldDisplayTrashIcon ? '' : 'd-none') +
      "><a href='#'><img  id='deleteFertilizationButton' date='" +
      startTime +
      "' src='" +
      require('images/icons/graph/trash_graph.svg') +
      "'/></a></div>";
    source = sourceObj.source;
    if (codeSource === 'sensorGraph' || !endTime) {
      hideEndDate = true;
    }
  } else {
    // rain event
    imgSrc = require('images/icons/graph/rain_icon.svg');
    if (codeSource === 'sensorGraph') {
      let source = sourceObj.source;
      deleteRow =
        "<div style='display: flex;align-self: end;padding-right: 8px;padding-left: 8px;justify-self: self-end' class=" +
        (shouldDisplayTrashIcon ? '' : 'd-none') +
        "><a href='#'><img id='deletePrecipitationButton' date='" +
        startTime +
        "' source='" +
        source +
        "' src='" +
        require('images/icons/graph/trash_graph.svg') +
        "'/></a></div>";
    }
  }
  source = source || '';

  // Redesigned card for the tray under the graph (Figma 12009:11615). Opt-in so
  // the planner popup and the graphs still on the old design are untouched.
  if (options.useNewDesign) {
    // The card reads "9 JUN" / "8:30 am"; the legacy strings above stay as-is.
    const cardTimezone = codeSource === 'planner' ? null : timezone;
    const cardBeg = formatCardDateTime(begDate, cardTimezone);
    const cardEnd = formatCardDateTime(endDate, cardTimezone);
    return buildGraphEventCard({
      eventIconSrc: GRAPH_CARD_ICONS[eventType] || GRAPH_CARD_ICONS.rain,
      amountText: value + ' ' + unit,
      extraText: extraText,
      pressure: pressure,
      pressureUnit: pressureUnit,
      pressureLabel: t ? t('pressure') : 'Pressure',
      pressureAlert: options.pressureAlert,
      alertIconSrc: GRAPH_CARD_ICONS.pressureAlert,
      t: t,
      deleteRow: deleteRow,
      timerIconSrc: GRAPH_CARD_ICONS.timer,
      begDateStr: cardBeg.date,
      begTimeStr: cardBeg.time,
      endDateStr: cardEnd.date,
      endTimeStr: cardEnd.time,
      hideEndDate: hideEndDate,
      sourceText: source,
      sourceIconSrc: sourceIconSrc,
      showArrow: options.showArrow,
    });
  }

  let popupHeight = !!extraRow ? '112px' : '96px';

  if (codeSource === 'planner') {
    firstRow =
      "<div style='height: " +
      popupHeight +
      "; pointer-events: none; flex-direction: column;position: absolute;box-shadow: 0px 4px 10px rgba(145, 157, 180, 0.19); border-radius: 8px' class='pr-2 pl-2 dropdown dropdown-hover dropdown-menu bg-white d-flex mt-1'>";
  }
  let modal =
    firstRow +
    "<div style='flex-direction: row ;display: flex;width: 100%;margin-bottom: 8px'>" +
    "<div class='align-self-center font-weight-normal'><img style='width: 21px;height: 21px' src='" +
    imgSrc +
    "'/></div>" +
    '<div>' +
    "<div class='pl-2 mr-1 text-grey71 font-weight-semi-bold align-self-center' style='font-size: 14px;white-space: nowrap'>" +
    value +
    ' ' +
    unit +
    '</div>' +
    extraRow +
    '</div>' +
    "<div><div class='align-self-top font-weight-semi-bold " +
    !!source +
    "' style='font-size: 14px;padding:0px 6px; background-color: #EEF1F6;color: #4D5674;border-radius: 3px; white-space: nowrap; margin-left: auto'>" +
    upper(source) +
    '</div></div>' +
    '</div>' +
    "<div style='width:100%;border-top: 1px solid #DCE0E8;align-self: center;'></div>" +
    "<div style='flex-direction: row ;display: flex; align-items:center; width: 100%;margin-top: 8px'>" +
    "<div class='font-weight-normal align-self-center'><img style='width: 21px;height: 21px' src='" +
    require('images/icons/graph/timer.svg') +
    "'/></div>" +
    "<div style='flex-direction: column'>" +
    "<div class='pl-2 text-grey71 align-self-center' style='font-size: 12px;white-space: nowrap'>" +
    fullBegDateStr +
    '</div>' +
    "<div class='pl-2 text-grey71 font-weight-semi-bold align-self-center' style='font-size: 14px;white-space: nowrap'>" +
    fullBegTimeStr +
    '</div>' +
    '</div>' +
    "<div class='pl-2 pr-2 text-grey71 align-self-center " +
    (hideEndDate ? 'd-none' : '') +
    "' style='font-size: 12px;white-space: nowrap'>-</div>" +
    '<div class=' +
    (hideEndDate ? 'd-none' : '') +
    " style='flex-direction: column;'>" +
    "<div class='pr-2 text-grey71 align-self-center' style='font-size: 12px;white-space: nowrap'>" +
    fullEndDateStr +
    '</div>' +
    "<div class='pr-2 text-grey71 font-weight-semi-bold align-self-center' style='font-size: 14px;white-space: nowrap;padding-right: 8px'>" +
    fullEndTimeStr +
    '</div>' +
    '</div>' +
    "<div style='width: 100%'></div>" +
    deleteRow +
    '</div>' +
    '</div>' +
    '</div>';

  return modal;
}
